import { useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTrafficLight, faSpinner } from '@/fontawesome.ts';
import Modal from '@/components/ui/Modal.tsx';
import { StocksStatusLightColor } from '@/models';
import type { CreateStocksStatusLightRequest } from '@/modelsAPI';
import { APIRoutes } from '@/services/api.routes.ts';
import { handleAPIError, showSuccessToast } from '@/utils/toasts.ts';

interface CreateStocksStatusLightModalProps {
  isOpen: boolean;
  onClose: () => void;
  onCreated: () => void;
}

export default function CreateStocksStatusLightModal({ isOpen, onClose, onCreated }: CreateStocksStatusLightModalProps) {
  const [color, setColor] = useState<StocksStatusLightColor>(StocksStatusLightColor.YELLOW);
  const [comment, setComment] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!comment.trim()) {
      return;
    }

    try {
      setIsSubmitting(true);

      const request: CreateStocksStatusLightRequest = {
        color,
        comment: comment.trim()
      };

      await APIRoutes.createStocksStatusLight(request);
      showSuccessToast('Börsen-Ampel erfolgreich gesetzt');

      // Reset form
      setColor(StocksStatusLightColor.YELLOW);
      setComment('');

      onCreated();
      onClose();
    } catch (err) {
      handleAPIError(err);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    if (!isSubmitting) {
      setColor(StocksStatusLightColor.YELLOW);
      setComment('');
      onClose();
    }
  };

  const getColorLabel = (c: StocksStatusLightColor): string => {
    switch (c) {
      case StocksStatusLightColor.GREEN:
        return 'Grün - Alles im grünen Bereich';
      case StocksStatusLightColor.YELLOW:
        return 'Gelb - Neutral';
      case StocksStatusLightColor.RED:
        return 'Rot - Vorsicht';
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title="Börsen-Ampel setzen"
      icon={<FontAwesomeIcon icon={faTrafficLight} className="w-5 h-5 text-blue-600" />}
      maxWidth="md"
      closeDisabled={isSubmitting}
    >
      <form onSubmit={handleSubmit} className="p-6">
        <div className="space-y-4">
          {/* Color Selection */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Ampel-Farbe *
            </label>
            <div className="space-y-2">
              {Object.values(StocksStatusLightColor).map((c) => (
                <label
                  key={c}
                  className={`flex items-center p-3 border rounded-lg cursor-pointer transition-colors ${
                    color === c
                      ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20'
                      : 'border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700'
                  }`}
                >
                  <input
                    type="radio"
                    name="color"
                    value={c}
                    checked={color === c}
                    onChange={(e) => setColor(e.target.value as StocksStatusLightColor)}
                    className="mr-3"
                  />
                  <div className="flex items-center flex-1">
                    <div
                      className={`w-4 h-4 rounded-full mr-3 ${
                        c === StocksStatusLightColor.GREEN
                          ? 'bg-green-500'
                          : c === StocksStatusLightColor.YELLOW
                          ? 'bg-yellow-400'
                          : 'bg-red-500'
                      }`}
                    />
                    <span className="text-gray-900 dark:text-white">{getColorLabel(c)}</span>
                  </div>
                </label>
              ))}
            </div>
          </div>

          {/* Comment */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Kommentar *
            </label>
            <textarea
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              rows={4}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-blue-500 focus:border-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              placeholder="Beschreiben Sie die aktuelle Marktlage..."
              required
              disabled={isSubmitting}
            />
          </div>
        </div>

        {/* Actions */}
        <div className="flex justify-end space-x-3 mt-6">
          <button
            type="button"
            onClick={handleClose}
            disabled={isSubmitting}
            className="px-4 py-2 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors cursor-pointer disabled:opacity-50"
          >
            Abbrechen
          </button>
          <button
            type="submit"
            disabled={isSubmitting || !comment.trim()}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
          >
            {isSubmitting && <FontAwesomeIcon icon={faSpinner} className="w-4 h-4 animate-spin" />}
            <span>{isSubmitting ? 'Wird gesetzt...' : 'Ampel setzen'}</span>
          </button>
        </div>
      </form>
    </Modal>
  );
}
